import { setContext } from '@apollo/client/link/context'
import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, Observable } from '@apollo/client/core'
import { createApolloProvider } from '@vue/apollo-option'
import ApolloLinkTimeout from 'apollo-link-timeout'

import { print } from 'graphql'
// Repo was moved to remix/multipart-parser if updated needed uninstall mjackson and install remix
import { parseMultipartStream, getMultipartBoundary } from '@mjackson/multipart-parser'

import { Auth } from '@aws-amplify/auth'
import Store from './stores'

const apiUri = process.env.VUE_APP_GRAPHQL_API_URI as string

// HTTP connection to the API
const httpLink = createHttpLink({
  uri: sessionStorage.getItem('customHeaders') ? apiUri.substring(0, apiUri.lastIndexOf('/')) + '/staging/graphql' : apiUri,
  credentials: 'include',
  fetchOptions: {
    mode: 'cors'
  },
  headers: JSON.parse(sessionStorage.getItem('customHeaders') ?? '{}')
})

const timeoutLink = new ApolloLinkTimeout(30000)

const authLink = setContext(async (req, prevContext) => {
  const jwtToken = (await Auth.currentSession()).getIdToken().getJwtToken()

  return {
    ...prevContext,
    headers: {
      ...prevContext.headers,
      authorization: jwtToken,
      tenant_name: Store.state.currentTenant !== null && Store.state.currentTenant !== undefined ? (Store.state.currentTenant as any).schemaName as string : null
    }
  }
})

const binaryLink = new ApolloLink((operation) => {
  const context = operation.getContext()

  if (!('key' in context)) {
    throw Error('Key not found in context')
  }

  return new Observable((observer) => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetch(sessionStorage.getItem('customHeaders') ? apiUri.substring(0, apiUri.lastIndexOf('/')) + '/staging/graphql' : apiUri, {
          method: 'POST',
          signal: controller.signal,
          credentials: 'include',
          mode: 'cors',
          headers: {
            ...operation.getContext().headers,
            ...JSON.parse(sessionStorage.getItem('customHeaders') ?? '{}'),
            'Content-Type': 'application/json',
            Accept: 'multipart/mixed'
          },
          body: JSON.stringify({
            query: print(operation.query),
            variables: operation.variables,
            operationName: operation.operationName
          })
        })

        let result: any

        const boundary = getMultipartBoundary(response.headers.get('content-type') ?? '')

        if (boundary === null || response.body === null) {
          const body = await response.text()

          try {
            observer.next(JSON.parse(body))
            observer.complete()
          } catch {
            observer.error(Error(`Unexpected ${response.status} response for '${context.key}': ${body.slice(0, 200)}`))
          }
          return
        }

        for await (const part of parseMultipartStream(response.body, { boundary })) {
          const contentType = part.mediaType ?? ''

          if (contentType.includes('application/json')) {
            result = await new Response(part.text).json()
          } else {
            const blob = await new Response(part.bytes, {
              headers: { 'Content-Type': contentType }
            }).blob()

            result ??= { data: { [context.key]: {} } }
            result.data[context.key].blob = blob
          }
        }

        if (result === undefined) {
          throw Error(`No parts found in the multipart response for '${context.key}'`)
        }

        observer.next(result)
        observer.complete()
      } catch (error) {
        observer.error(error)
      }
    })()

    return () => controller.abort()
  })
})

// Cache implementation
const cache = new InMemoryCache()

// Create the apollo client
export const apolloClient = new ApolloClient({
  link: authLink.concat(timeoutLink.concat(
    ApolloLink.split(
      (operation) => operation.getContext().binary === true,
      binaryLink,
      httpLink
    )
  )),
  cache
})

export default createApolloProvider({
  defaultClient: apolloClient
})
